import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, ArrowLeft, Phone, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useSiteSettings } from '../context/SiteSettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const { t } = useTranslation();
  const { authBg, logo } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register'
    : searchParams.get('mode') === 'reset' ? 'reset'
    : searchParams.get('mode') === 'forgot' ? 'forgot'
    : 'login';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register, loginWithGoogle, resendVerification } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setSuccess(t('auth.resetSent'));
      } else if (mode === 'reset') {
        const token = searchParams.get('token');
        if (!token) { setError(t('auth.invalidLink')); setLoading(false); return; }
        await axios.post(`${API}/auth/reset-password`, { token, password });
        setSuccess(t('auth.resetSuccess'));
        setTimeout(() => setMode('login'), 2000);
      } else if (mode === 'register') {
        const result = await register(email, password, name, phone);
        if (result?.needsVerification) {
          setPendingVerificationEmail(result.email || email);
          setMode('check-inbox');
        } else {
          navigate('/dashboard');
        }
      } else {
        await login(email, password);
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'EMAIL_NOT_VERIFIED') {
        setPendingVerificationEmail(email);
        setMode('check-inbox');
      } else {
        setError(detail || t('auth.errorOccurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  const [pendingVerificationEmail, setPendingVerificationEmail] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);
  const doResend = async () => {
    if (!pendingVerificationEmail || resendCooldown > 0) return;
    try {
      await resendVerification(pendingVerificationEmail);
      setSuccess(t('auth.verifyResent', { defaultValue: 'Email renvoyé. Vérifiez votre boîte de réception.' }));
      setResendCooldown(45);
    } catch {
      setError(t('auth.verifyResendError', { defaultValue: "Impossible d'envoyer l'email. Réessayez." }));
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left panel - form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          <button
            data-testid="auth-back-btn"
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-[#737373] hover:text-[#1A3C34] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>

          <div
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mb-8 cursor-pointer"
            data-testid="auth-logo-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_instant-access-space/artifacts/jtfp03jx_Flexic.jpg"
              alt="Flexi-location"
              className="h-9 w-auto rounded-md"
            />
          </div>

          <h1 className="font-heading text-3xl font-semibold text-[#1A3C34]">
            {mode === 'register' ? t('auth.registerTitle')
              : mode === 'forgot' ? t('auth.forgotTitle')
              : mode === 'reset' ? t('auth.resetTitle')
              : mode === 'check-inbox' ? t('auth.checkInboxTitle', { defaultValue: 'Vérifiez votre boîte mail' })
              : t('auth.loginTitle')}
          </h1>
          <p className="mt-2 text-sm text-[#737373]">
            {mode === 'register' ? t('auth.registerSubtitle')
              : mode === 'forgot' ? t('auth.forgotSubtitle')
              : mode === 'reset' ? t('auth.resetSubtitle')
              : mode === 'check-inbox' ? t('auth.checkInboxSubtitle', { defaultValue: "Un lien de confirmation vient d'être envoyé. Cliquez dessus pour activer votre compte." })
              : t('auth.loginSubtitle')}
          </p>

          {/* CHECK-INBOX view — replaces the form entirely after a successful
              registration (or a login attempt on an unverified account). */}
          {mode === 'check-inbox' && (
            <div className="mt-8 space-y-4" data-testid="check-inbox-view">
              <div className="p-4 bg-[#F0EFEA] border border-[#E2E2DF] rounded-md text-sm text-[#1A3C34]">
                <div className="font-semibold mb-1">📩 {t('auth.emailSentTo', { defaultValue: 'Email envoyé à' })}</div>
                <div className="font-mono text-[13px] break-all">{pendingVerificationEmail}</div>
              </div>
              <p className="text-xs text-[#737373]">
                {t('auth.checkInboxHint', { defaultValue: "Pensez à consulter votre dossier Spam/Indésirables. Le lien expire dans 24 heures." })}
              </p>
              <Button
                onClick={doResend}
                disabled={resendCooldown > 0}
                variant="outline"
                className="w-full h-11 border-[#C77D63]/40 text-[#C77D63] hover:bg-[#C77D63]/10"
                data-testid="resend-verification-btn"
              >
                {resendCooldown > 0
                  ? t('auth.resendIn', { defaultValue: 'Renvoyer dans {{s}}s', s: resendCooldown })
                  : t('auth.resendVerification', { defaultValue: "Renvoyer l'email de confirmation" })}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="w-full text-[#1A3C34]"
                data-testid="back-to-login-from-inbox"
              >
                {t('auth.backToLogin', { defaultValue: 'Retour à la connexion' })}
              </Button>
            </div>
          )}

          {/* Google login - only for login/register */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <Button
                data-testid="google-login-btn"
                variant="outline"
                onClick={loginWithGoogle}
                className="w-full mt-6 h-12 border-[#E2E2DF] text-[#1A3C34] hover:bg-[#F0EFEA] rounded-md font-medium"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E2DF]" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#F9F9F7] px-3 text-[#737373]">{t('auth.or')}</span>
                </div>
              </div>
            </>
          )}

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {success && (
            <div data-testid="auth-success" className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className={`space-y-4 ${mode === 'check-inbox' ? 'hidden' : ''}`} data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#1A3C34]">{t('auth.name')}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="name"
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth.yourName')}
                    className="pl-10 h-12 border-[#E2E2DF] rounded-md focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34]"
                    required
                  />
                </div>
              </div>
            )}
            {mode === 'register' && (
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-[#1A3C34]">
                  {t('auth.phoneSms')}{' '}
                  <span className="text-xs font-normal text-[#737373]">
                    {t('auth.phoneOptional', '(optionnel — pour recevoir les codes par SMS)')}
                  </span>
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="auth-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+32 470 12 34 56"
                    className="pl-10 h-12 border-[#E2E2DF] rounded-md focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34]"
                  />
                </div>
              </div>
            )}

            {/* Email - shown for login, register, forgot */}
            {mode !== 'reset' && (
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#1A3C34]">{t('auth.email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="auth-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.yourEmail')}
                    className="pl-10 h-12 border-[#E2E2DF] rounded-md focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34]"
                    required
                  />
                </div>
              </div>
            )}

            {/* Password - shown for login, register, reset */}
            {mode !== 'forgot' && (
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-[#1A3C34]">
                  {mode === 'reset' ? t('auth.newPassword') : t('auth.password')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="auth-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'reset' ? t('auth.newPasswordPlaceholder') : t('auth.yourPassword')}
                    className="pl-10 pr-10 h-12 border-[#E2E2DF] rounded-md focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34]"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    data-testid="auth-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A3C34] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot password link - only on login */}
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                  className="text-sm text-[#C77D63] hover:text-[#1A3C34] transition-colors"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full h-12 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-md font-medium"
            >
              {loading ? t('common.loading')
                : mode === 'register' ? t('auth.submitRegister')
                : mode === 'forgot' ? t('auth.sendLink')
                : mode === 'reset' ? t('auth.resetSubmit')
                : t('auth.submitLogin')}
            </Button>
          </form>

          {/* Footer links */}
          <div className="mt-6 text-center text-sm text-[#737373]">
            {mode === 'forgot' || mode === 'reset' ? (
              <button
                data-testid="auth-back-to-login"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="text-[#1A3C34] font-medium hover:text-[#C77D63] transition-colors"
              >
                {t('auth.backToLogin')}
              </button>
            ) : (
              <p>
                {mode === 'register' ? t('auth.alreadyAccount') : t('auth.noAccount')}{' '}
                <button
                  data-testid="auth-toggle-mode"
                  onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setError(''); setSuccess(''); }}
                  className="text-[#1A3C34] font-medium hover:text-[#C77D63] transition-colors"
                >
                  {mode === 'register' ? t('auth.submitLogin') : t('nav.register')}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Right panel - image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src={authBg}
          alt="Flexi-location"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3C34] via-[#1A3C34]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/95 backdrop-blur-sm p-8 rounded-xl shadow-lg">
            <p className="font-heading text-xl font-bold text-[#1A3C34]">
              {t('auth.imageQuote')}
            </p>
            <p className="mt-3 text-sm text-[#737373] leading-relaxed">
              {t('auth.imageDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
